import { useEffect, useMemo } from 'react';
import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    BuyOutDecision,
    DecisionCode,
    IntercompanyPreviewKind,
    IntercompanyPreviewLine,
    ReasonCode,
    ReturnItem,
    ReturnOrderLine,
} from 'returnCenter-api';
import {
    clearIntercompanyPreview,
    fetchIntercompanyPreview,
    intercompanyDecisionVerdict,
    intercompanySelectors,
    resolveIntercompanyDecisionCodes,
} from 'store/reducers/intercompanySlice';
import useAppSelector from 'hooks/useAppSelector';
import useAppDispatch from 'hooks/useAppDispatch';
import { ExtendedReturnItem, returnSelectors } from 'store/reducers/returnSlice';
import useOpenLinkedReturn from 'hooks/useOpenLinkedReturn';
import { HandleReturnItemChange } from 'components/Orders/Order';
import IntercompanyDecisionCodeCell from 'components/TableCells/IntercompanyDecisionCodeCell';
import IntercompanyReasonCodeCell from 'components/TableCells/IntercompanyReasonCodeCell';
import BuyOutDecisionCell from 'components/TableCells/BuyOutDecisionCell';
import SwitchToggle from 'components/ui/Switch';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';

type IntercompanyPanelProps = {
    handleReturnItemChange: HandleReturnItemChange;
    disabled?: boolean;
};

type PanelRow = {
    line: ReturnOrderLine;
    item: ReturnItem;
};

// The id updateReturnItem looks a row up by: splitId when the row has one, else id.
const rowId = (item: ReturnItem): string | number => (item as ExtendedReturnItem).splitId ?? item.id ?? -1;

/**
 * The lines on this return that a sister company owes a credit for, grouped by that company.
 *
 * This is the "handle both credits from one view" half of the flow: the operator settles how the
 * customer is credited in the table above and how the supplying company is credited here, then
 * completes once. The intercompany returns themselves are raised server-side after the customer
 * credit is confirmed, which is why nothing here creates anything.
 *
 * Internal prices are read from the supplying companies' MDC orders through the preview endpoint.
 * Their real worth is not the figures but the warnings: a line that cannot be priced says so here,
 * while the operator can still act on it. The same failure found during crediting arrives after the
 * customer has already been credited, which is the one moment it is too late to be useful.
 */
const IntercompanyPanel = ({ handleReturnItemChange, disabled = false }: IntercompanyPanelProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { open: openLinkedReturn, canOpen } = useOpenLinkedReturn();
    const returnSlip = useAppSelector(returnSelectors.selectReturnSlip);

    const groups = useMemo(() => {
        const byCompany = new Map<string, PanelRow[]>();

        for (const group of returnSlip?.returnItems ?? []) {
            for (const line of group.orderLines ?? []) {
                if (!line.isIntercompanyEligible || !line.supplyingCompany) continue;

                for (const item of line.returnItems ?? []) {
                    // Batching and intercompany crediting are alternatives for the same line, and
                    // the operator picks per row. A row placed in a batch travels back physically
                    // and is settled there, so it drops out here as soon as that choice is made.
                    if (item.batchReturnId) continue;

                    // Rows with nothing entered yet are shown on purpose. The operator needs to
                    // know a sister company is involved before deciding quantities, not after —
                    // and nothing is created for a row that stays at zero.
                    const rows = byCompany.get(line.supplyingCompany) ?? [];
                    rows.push({ line, item });
                    byCompany.set(line.supplyingCompany, rows);
                }
            }
        }

        return [...byCompany.entries()].sort(([a], [b]) => a.localeCompare(b));
    }, [returnSlip]);

    // Built from the return, not the preview, so a failed preview fetch does not block the decision.
    const buyOutGroups = useMemo(() => {
        const byCompany = new Map<string, PanelRow[]>();

        for (const group of returnSlip?.returnItems ?? []) {
            for (const line of group.orderLines ?? []) {
                if (!line.isBuyOutEligible || !line.supplyingCompany) continue;

                for (const item of line.returnItems ?? []) {
                    if (item.batchReturnId || item.intercompanyReturnId) continue;

                    const rows = byCompany.get(line.supplyingCompany) ?? [];
                    rows.push({ line, item });
                    byCompany.set(line.supplyingCompany, rows);
                }
            }
        }

        return [...byCompany.entries()].sort(([a], [b]) => a.localeCompare(b));
    }, [returnSlip]);

    const linksByCompany = useMemo(() => {
        const map = new Map<string, NonNullable<typeof returnSlip>['intercompanyReturns']>();
        for (const link of returnSlip?.intercompanyReturns ?? []) {
            if (!link.supplyingCompany) continue;
            map.set(link.supplyingCompany, [link]);
        }
        return map;
    }, [returnSlip]);

    // Prices and warnings come from the supplying companies' MDC orders, one ERP round-trip each.
    // Fetched once when there is something to price: the unit price does not change as the operator
    // edits, so totals are recomputed locally rather than asking the ERP again on every keystroke.
    //
    // Kept in the store because completing the return has to see the warnings too.
    const preview = useAppSelector(intercompanySelectors.selectPreview);
    const pricesFailed = useAppSelector(intercompanySelectors.selectFailed);
    const returnId = returnSlip?.id;
    const hasGroups = groups.length > 0;

    const hasBuyOutRows = buyOutGroups.length > 0;

    useEffect(() => {
        if (!returnId || (!hasGroups && !hasBuyOutRows)) return;
        dispatch(fetchIntercompanyPreview(returnId));
    }, [dispatch, returnId, hasGroups, hasBuyOutRows]);

    const buyerCompany = useMemo(
        () => preview.find((group) => group.kind === IntercompanyPreviewKind.BuyOut)?.buyerCompany ?? null,
        [preview],
    );

    useEffect(
        () => () => {
            dispatch(clearIntercompanyPreview());
        },
        [dispatch, returnId],
    );

    const pricesByRowId = useMemo(() => {
        const byRow = new Map<number, IntercompanyPreviewLine>();
        for (const group of preview) {
            for (const line of group.lines ?? []) {
                if (line.returnItemRowId != null) byRow.set(line.returnItemRowId, line);
            }
        }
        return byRow;
    }, [preview]);

    const liveSuggestions = useAppSelector(intercompanySelectors.selectLiveSuggestions);

    // The supplying company's rules key on the reason code, so a change there makes the suggestion
    // stale. Re-ask, the same way the customer decision code already re-evaluates. Only the reason
    // codes are in the signature: nothing else the rules use changes while editing.
    const reasonCodeSignature = useMemo(
        () =>
            groups
                .flatMap(([, rows]) =>
                    rows.map(
                        ({ item }) =>
                            `${item.id}:${item.reasonCode ?? 0}` +
                            `:${item.intercompanyReasonCodeIsManual ? (item.intercompanyReasonCode ?? 0) : ''}`,
                    ),
                )
                .join('|'),
        [groups],
    );

    useEffect(() => {
        if (preview.length === 0) return;

        const byRow = new Map<number, IntercompanyPreviewLine>();
        for (const group of preview) {
            for (const line of group.lines ?? []) {
                if (line.returnItemRowId != null) byRow.set(line.returnItemRowId, line);
            }
        }

        // The account the supplying company's rules key on is that company's own, taken from its
        // preview group. Sending one account for every company resolves the second company's rows
        // against the first company's rule set.
        const groupByCompany = new Map(
            preview.filter((g) => g.supplyingCompany).map((g) => [g.supplyingCompany as string, g]),
        );

        const items = groups.flatMap(([company, rows]) =>
            rows
                // A credited row is done. Rows the operator has taken over still come along: the
                // decision code they picked is left alone by the server, but the verdict on the
                // reason code is precisely what they need back after changing it.
                .filter(({ item }) => item.id != null && !item.intercompanyReturnId)
                .map(({ line, item }) => ({
                    returnItemRowId: item.id as number,
                    supplyingCompany: company,
                    reasonCode: item.reasonCode ?? 0,
                    chosenReasonCode: item.intercompanyReasonCodeIsManual
                        ? (item.intercompanyReasonCode ?? null)
                        : null,
                    supplyingCustomerCode: groupByCompany.get(company)?.supplyingCustomerCode ?? null,
                    supplyingCustomerGroupCode:
                        groupByCompany.get(company)?.supplyingCustomerGroupCode ?? null,
                    mdcOrderNumber: byRow.get(item.id as number)?.mdcOrderNumber ?? null,
                    mdcOrderType: byRow.get(item.id as number)?.mdcOrderType ?? null,
                    dispatchDate: line.dispatchDate ?? null,
                })),
        );

        if (items.length === 0) return;

        // Debounced: a bulk reason-code change would otherwise fire one request per row.
        const timer = setTimeout(() => {
            dispatch(
                resolveIntercompanyDecisionCodes({
                    arrivalDate: returnSlip?.arrivalDate?.utc ?? null,
                    items,
                }),
            );
        }, 400);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, reasonCodeSignature, preview]);

    /** The decision codes each supplying company offers — theirs, not the selling company's. */
    const decisionCodesByCompany = useMemo(() => {
        const byCompany = new Map<string, DecisionCode[]>();
        for (const group of preview) {
            if (group.supplyingCompany) byCompany.set(group.supplyingCompany, group.availableDecisionCodes ?? []);
        }
        return byCompany;
    }, [preview]);

    /** Likewise the reason codes — the sets overlap between group companies rather than match. */
    const reasonCodesByCompany = useMemo(() => {
        const byCompany = new Map<string, ReasonCode[]>();
        for (const group of preview) {
            if (group.supplyingCompany) byCompany.set(group.supplyingCompany, group.availableReasonCodes ?? []);
        }
        return byCompany;
    }, [preview]);

    const liveReasonCodes = useAppSelector(intercompanySelectors.selectLiveReasonCodes);
    const liveDecisions = useAppSelector(intercompanySelectors.selectLiveDecisions);

    /** Pairs set to manual review: the return is raised over there, but nobody credits it here. */
    const reviewOnlyCompanies = useMemo(
        () =>
            new Set(
                preview
                    .filter((group) => group.autoCredit === false && group.supplyingCompany)
                    .map((group) => group.supplyingCompany as string),
            ),
        [preview],
    );

    if (!hasGroups && !hasBuyOutRows) return null;

    return (
        <Wrapper column gap="1rem">
            <Text as="h3">{t('intercompany.title', 'Intercompany credits')}</Text>
            <Text as="p" light small>
                {t(
                    'intercompany.description',
                    'These lines were supplied by another group company. Completing the return also credits them.',
                )}
            </Text>
            {pricesFailed && (
                <Text as="p" light small>
                    {t(
                        'intercompany.pricesUnavailable',
                        'Internal prices could not be read from the supplying company right now. The decisions below can still be set.',
                    )}
                </Text>
            )}

            {groups.map(([company, rows]) => {
                const link = linksByCompany.get(company)?.[0];

                return (
                    <CompanyCard key={company} column gap="0.75rem">
                        <Flex align="center" gap="0.75rem">
                            <Text as="h4">{company}</Text>
                            {reviewOnlyCompanies.has(company) && (
                                <ReviewBadge
                                    title={t(
                                        'intercompany.manualReviewExplained',
                                        'This pair is configured for manual review. The return is created here, but someone in {{company}} completes and credits it.',
                                        { company },
                                    )}
                                >
                                    {t('intercompany.manualReview', 'Reviewed by them')}
                                </ReviewBadge>
                            )}
                            {link?.returnId && (
                                <LinkButton
                                    type="button"
                                    disabled={!canOpen(link)}
                                    title={
                                        canOpen(link)
                                            ? undefined
                                            : t('intercompany.noAccessToCompany', 'You do not have access to this company')
                                    }
                                    onClick={() => openLinkedReturn(link)}
                                >
                                    {t('intercompany.openReturn', 'Open return {{id}}', { id: link.returnId })}
                                </LinkButton>
                            )}
                        </Flex>

                        <RowTable>
                            <thead>
                                <tr>
                                    <th>{t('sku', 'SKU')}</th>
                                    <th>{t('intercompany.mdcOrder', 'MDC order')}</th>
                                    <th>{t('quantity', 'Qty')}</th>
                                    <th>{t('intercompany.unitPrice', 'Internal price')}</th>
                                    <th>{t('intercompany.reasonCode', 'Reason code')}</th>
                                    <th>{t('intercompany.decisionCode', 'Decision code')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(({ line, item }) => {
                                    const price = item.id != null ? pricesByRowId.get(item.id) : undefined;
                                    const quantity = item.quantityInReturn ?? 0;

                                    // The live answer supersedes the preview's, so a reason code
                                    // the operator has just fixed stops complaining immediately.
                                    const live = item.id != null ? liveReasonCodes[item.id] : undefined;
                                    const effectiveReasonCode = live
                                        ? live.effective
                                        : (price?.effectiveReasonCode ?? 0);
                                    const reasonProblem = live ? live.problem : (price?.reasonCodeProblem ?? null);

                                    const decision = intercompanyDecisionVerdict(
                                        item,
                                        price,
                                        item.id != null ? liveDecisions[item.id] : undefined,
                                    );

                                    return (
                                        <tr key={`${line.orderNumber}-${line.orderLineNumber}-${rowId(item)}`}>
                                            <td>{line.sku}</td>
                                            <td>
                                                {price?.mdcOrderNumber ?? line.supplyingCompanyOrderNumber ?? '-'}
                                                {price?.mdcOrderLine ? `/${price.mdcOrderLine}` : ''}
                                            </td>
                                            <td>{quantity}</td>
                                            <td>
                                                {price?.pricingProblem ? (
                                                    <Problem title={price.pricingProblem}>
                                                        {t('intercompany.cannotPrice', 'Cannot be priced')}
                                                    </Problem>
                                                ) : price?.unitPrice != null ? (
                                                    <>
                                                        {price.unitPrice.toFixed(2)}
                                                        <Total>{(price.unitPrice * quantity).toFixed(2)}</Total>
                                                    </>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                            <td>
                                                <IntercompanyReasonCodeCell
                                                    item={item}
                                                    itemId={rowId(item)}
                                                    orderline={line}
                                                    handleReturnItemChange={handleReturnItemChange}
                                                    disabled={disabled || !!item.intercompanyReturnId}
                                                    reasonCodes={reasonCodesByCompany.get(company) ?? []}
                                                    inheritedReasonCode={effectiveReasonCode}
                                                />
                                                {/* An unknown reason code is upstream of the
                                                    decision: no rule over there can match it, and
                                                    their credit line would name it by number. So it
                                                    belongs next to the dropdown that fixes it. */}
                                                {reasonProblem && (
                                                    <Problem title={reasonProblem}>
                                                        {t(
                                                            'intercompany.unknownReasonCode',
                                                            'Not a reason code in {{company}}',
                                                            { company },
                                                        )}
                                                    </Problem>
                                                )}
                                            </td>
                                            <td>
                                                <IntercompanyDecisionCodeCell
                                                    item={item}
                                                    itemId={rowId(item)}
                                                    orderline={line}
                                                    handleReturnItemChange={handleReturnItemChange}
                                                    disabled={disabled || !!item.intercompanyReturnId}
                                                    decisionCodes={decisionCodesByCompany.get(company) ?? []}
                                                    suggestedDecisionCodeId={
                                                        item.id != null && item.id in liveSuggestions
                                                            ? liveSuggestions[item.id]
                                                            : price?.suggestedDecisionCodeId
                                                    }
                                                />
                                                {/* A missing decision code belongs next to the
                                                    dropdown that fixes it, not in the price column.
                                                    Waiting on a reason code is not a fault — it is
                                                    every fresh row — so it stays quiet and grey. */}
                                                {decision.problem ? (
                                                    <Problem title={decision.problem}>
                                                        {t('intercompany.noDecisionCode', 'No decision code resolves')}
                                                    </Problem>
                                                ) : decision.awaitingReasonCode ? (
                                                    <Hint>
                                                        {t('intercompany.awaitingReasonCode', 'Set once a reason code is chosen')}
                                                    </Hint>
                                                ) : null}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </RowTable>
                    </CompanyCard>
                );
            })}

            {hasBuyOutRows && (
                <>
                    <Text as="h3">{t('buyOut.title', 'Goods from other companies')}</Text>
                    <Text as="p" light small>
                        {buyerCompany
                            ? t(
                                  'buyOut.description',
                                  'These lines were delivered by a company that is not part of this return. The goods are here, but the sale that brought them was between two other companies. Decide whether {{buyer}} buys them at purchase value or leaves them.',
                                  { buyer: buyerCompany },
                              )
                            : t(
                                  'buyOut.descriptionNoBuyer',
                                  'These lines were delivered by a company that is not part of this return. The goods are here, but the sale that brought them was between two other companies. Decide whether they are bought at purchase value or left.',
                              )}
                    </Text>
                </>
            )}

            {buyOutGroups.map(([company, rows]) => (
                <CompanyCard key={`buyout-${company}`} column gap="0.75rem">
                    <Flex align="center" gap="0.75rem">
                        <Text as="h4">{company}</Text>
                        <Hint>
                            {t('buyOut.notCredited', 'Not credited — they are not part of this return')}
                        </Hint>
                    </Flex>

                    <RowTable>
                        <thead>
                            <tr>
                                <th>{t('sku', 'SKU')}</th>
                                <th>{t('buyOut.originOrder', 'Their order')}</th>
                                <th>{t('quantity', 'Qty')}</th>
                                <th>{t('buyOut.purchaseValue', 'Purchase value')}</th>
                                <th>{t('buyOut.decision', 'Buy out')}</th>
                                <th>{t('returnToStock', 'Return to stock')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(({ line, item }) => {
                                const price = item.id != null ? pricesByRowId.get(item.id) : undefined;
                                const quantity = item.quantityInReturn ?? 0;

                                return (
                                    <tr key={`buyout-${line.orderNumber}-${line.orderLineNumber}-${rowId(item)}`}>
                                        <td>{line.sku}</td>
                                        <td>
                                            {line.supplyingCompanyOrderNumber ?? '-'}
                                            {line.supplyingCompanyOrderLine
                                                ? `/${line.supplyingCompanyOrderLine}`
                                                : ''}
                                        </td>
                                        <td>{quantity}</td>
                                        <td>
                                            {price?.purchaseValueUnknown ? (
                                                <Hint
                                                    title={t(
                                                        'buyOut.noValueExplained',
                                                        'No purchase value was recorded for this line. It can still be bought, and the goods will be received, but at zero value.',
                                                    )}
                                                >
                                                    {t('buyOut.noValue', 'No value — buys at 0')}
                                                </Hint>
                                            ) : price?.unitPrice != null ? (
                                                <>
                                                    {price.unitPrice.toFixed(2)}
                                                    <Total>{(price.unitPrice * quantity).toFixed(2)}</Total>
                                                </>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                        <td>
                                            <BuyOutDecisionCell
                                                item={item}
                                                itemId={rowId(item)}
                                                orderline={line}
                                                handleReturnItemChange={handleReturnItemChange}
                                                disabled={disabled}
                                            />
                                            {price?.reasonCodeProblem && (
                                                <Problem title={price.reasonCodeProblem}>
                                                    {t('buyOut.reasonCodeProblem', 'Reason code unknown to the buyer')}
                                                </Problem>
                                            )}
                                        </td>
                                        <td>
                                            <SwitchToggle
                                                checked={item.buyOutReturnToStock ?? true}
                                                disabled={disabled || item.buyOutDecision !== BuyOutDecision.Buy}
                                                onHandleToggle={(checked) =>
                                                    handleReturnItemChange(
                                                        line.orderNumber ?? '',
                                                        line.sku ?? '',
                                                        line.orderLineNumber ?? '',
                                                        rowId(item),
                                                        'buyOutReturnToStock',
                                                        checked,
                                                    )
                                                }
                                                size="medium"
                                                tabIndex={0}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </RowTable>
                </CompanyCard>
            ))}
        </Wrapper>
    );
};

export default IntercompanyPanel;

const Wrapper = styled(Flex)`
    margin-top: 1rem;
`;

const CompanyCard = styled(Flex)`
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 4px;
    padding: 1rem;
`;

const RowTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        text-align: left;
        padding: 0.4rem 0.5rem;
        border-bottom: 1px solid ${({ theme }) => theme.colors.border};
        vertical-align: middle;
    }

    th {
        font-weight: 600;
        white-space: nowrap;
    }
`;

const Total = styled.span`
    display: block;
    font-size: 0.85em;
    opacity: 0.7;
`;

const Hint = styled.span`
    display: block;
    margin-top: 0.25rem;
    font-size: 0.85em;
    opacity: 0.6;
`;

const Problem = styled.span`
    color: ${({ theme }) => theme.colors.error ?? 'inherit'};
    cursor: help;
    text-decoration: underline dotted;
`;

const ReviewBadge = styled.span`
    font-size: 0.75em;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0.15rem 0.4rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 3px;
    opacity: 0.75;
    cursor: help;
    white-space: nowrap;
`;

const LinkButton = styled.button`
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.accent};

    &:disabled {
        color: ${({ theme }) => theme.colors.textDisabled};
        cursor: not-allowed;
        text-decoration: none;
    }
`;
